import { createContext, PropsWithChildren, useContext, useMemo } from "react";
import { useProfile } from "@/components/Auth/ProfileProvider";

export type OrganizationBrandingState = {
  colorPreset: string;
  typographyPreset: string;
  logoUrl?: string | null;
  logoDarkUrl?: string | null;
  customized: boolean;
  updatedAt?: unknown;
  redesignEnabled: boolean;
  resolved: boolean;
};

export const PDS_BRAND: OrganizationBrandingState = {
  colorPreset: "pds",
  typographyPreset: "pds",
  logoUrl: null,
  logoDarkUrl: null,
  customized: false,
  updatedAt: null,
  redesignEnabled: false,
  resolved: false,
};

const OrganizationBrandContext = createContext(PDS_BRAND);

export function OrganizationBrandProvider({ children }: PropsWithChildren) {
  const { profile, loading } = useProfile();
  const organization = profile?.organization;
  const branding = organization?.branding;

  const value = useMemo<OrganizationBrandingState>(
    () => ({
      colorPreset: branding?.colorPreset || "pds",
      typographyPreset: branding?.typographyPreset || "pds",
      logoUrl: branding?.logoUrl || null,
      logoDarkUrl: branding?.logoDarkUrl || branding?.logoUrl || null,
      customized: Boolean(branding?.customized),
      updatedAt: branding?.updatedAt || null,
      // Staff and superusers without an organization use the default PDS redesign.
      // Organization accounts (including impersonation) keep their org's setting.
      redesignEnabled: Boolean(
        organization?.redesignEnabled ||
          ((profile?.isSuperuser || profile?.isStaff) && !profile.organizationId)
      ),
      resolved: Boolean(profile) || !loading,
    }),
    [branding, loading, organization?.redesignEnabled, profile]
  );

  return (
    <OrganizationBrandContext.Provider value={value}>
      {children}
    </OrganizationBrandContext.Provider>
  );
}

export const useOrganizationBrand = () => useContext(OrganizationBrandContext);
